import React, { useEffect, useMemo, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import Langs, { Localize } from "../../app/lang/langs";
import { VLearnTypeIcon, formatVLearnDuration, vlearnType } from "./vlearnUi";

/**
 * V-Learning — the course curriculum, as the learner's table of contents.
 *
 * Sections collapse, and the one holding the open lesson is expanded for you. A course with
 * nine sections is otherwise a wall of forty rows in which the reader has to find their own
 * place every time they finish something.
 *
 * `locked` is not computed here. The server decides it from the preceding mandatory lesson's
 * status and sends it on the row, and it refuses the lesson again when it is opened. What
 * this component does with the flag is purely presentational: grey the row, remove it from
 * the tab order, and say why in a tooltip — because a row that simply does nothing when
 * clicked reads as a broken page rather than as a rule.
 */
export default function CurriculumSidebar({ sections, currentLessonId, onSelectLesson, sequential }) {

    // Memoised because `requiredOnly` depends on it: a bare `sections || []` is a new array
    // identity on every render, which would recompute the memo every time.
    const list = useMemo(() => sections || [], [sections])

    /*
     * Does this COURSE have any required lessons?
     *
     * The same question `computeCourseProgress` asks, and the answer has to be computed
     * across the whole course rather than per section — a course with one required lesson
     * counts only that one, wherever it sits.
     */
    const requiredOnly = useMemo(
        () => list.some(section => (section.lessons || [])
            .some(lesson => Number(lesson.is_mandatory))),
        [list])

    const currentSectionId = useMemo(() => {
        const owner = (sections || []).find(section =>
            (section.lessons || []).some(lesson => lesson.id === currentLessonId))
        return owner ? owner.id : null
    }, [sections, currentLessonId])

    /**
     * Only the sections the reader has explicitly toggled. Everything else falls back to the
     * derived default below, so moving to a lesson in another section opens that section
     * without closing the ones the reader had opened to browse.
     */
    const [overrides, setOverrides] = useState({})

    useEffect(() => {
        if (!currentSectionId) return
        setOverrides(previous => ({ ...previous, [currentSectionId]: true }))
    }, [currentSectionId])

    const isExpanded = (section, index) => {
        if (Object.prototype.hasOwnProperty.call(overrides, section.id)) return !!overrides[section.id]
        return currentSectionId ? section.id === currentSectionId : index === 0
    }

    return (
        <Box component="nav" aria-label={Localize('VLEARN_CURRICULUM')}>
            {list.map((section, index) => {
                const lessons = section.lessons || []

                /*
                 * Counted the way completion is counted, not the way the list looks.
                 *
                 * `computeCourseProgress` counts only MANDATORY lessons when a course has
                 * any. This used to count them all, so a course with one required lesson
                 * among five reported "3 of 5 completed" here and 100% on the card — two
                 * true numbers measuring different things, which reads as a bug.
                 *
                 * `requiredOnly` is decided at COURSE level, not per section, because that is
                 * where the rule applies: a section of purely optional lessons in a course
                 * that has required ones elsewhere contributes nothing to completion, and
                 * must not claim otherwise.
                 */
                const counted = requiredOnly
                    ? lessons.filter(lesson => Number(lesson.is_mandatory))
                    : lessons
                const done = counted.filter(lesson => lesson.my_status === 'completed').length
                const optionalDone = requiredOnly
                    ? lessons.filter(lesson => !Number(lesson.is_mandatory)
                        && lesson.my_status === 'completed').length
                    : 0

                return (
                    <Accordion
                        key={section.id}
                        disableGutters
                        elevation={0}
                        expanded={isExpanded(section, index)}
                        onChange={(event, open) =>
                            setOverrides(previous => ({ ...previous, [section.id]: open }))}
                        sx={{
                            backgroundColor: 'transparent',
                            border: 0,
                            borderBottom: '1px solid rgba(0,0,0,0.07)',
                            '&:before': { display: 'none' },
                            '&:last-of-type': { borderBottom: 0 }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                            sx={{
                                px: 1.5, minHeight: 52,
                                '& .MuiAccordionSummary-content': { my: 1, minWidth: 0 },
                                '&:focus-visible': {
                                    outline: '2px solid var(--orange, rgb(255,144,21))',
                                    outlineOffset: '-2px', backgroundColor: 'transparent'
                                }
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography component="h3" sx={{ ...vlearnType.cardTitle }}>
                                    {section.title}
                                </Typography>
                                <Typography sx={{ ...vlearnType.meta, mt: 0.25 }}>
                                    {/* The label names its own denominator. "1 of 2
                                        completed" next to a card reading 100% is two true
                                        numbers measuring different things. */}
                                    <Langs
                                        str={requiredOnly
                                            ? 'VLEARN_SECTION_COMPLETION_REQUIRED'
                                            : 'VLEARN_SECTION_COMPLETION'}
                                        params={{ done, total: counted.length }} />
                                    {optionalDone ? (
                                        <Langs
                                            str={optionalDone === 1
                                                ? 'VLEARN_SECTION_OPTIONAL_DONE_ONE'
                                                : 'VLEARN_SECTION_OPTIONAL_DONE_MANY'}
                                            params={{ n: optionalDone }} />
                                    ) : null}
                                </Typography>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0, pb: 1 }}>
                            <List disablePadding>
                                {lessons.map(lesson => (
                                    <LessonRow
                                        key={lesson.id}
                                        lesson={lesson}
                                        sequential={sequential}
                                        isCurrent={lesson.id === currentLessonId}
                                        onSelect={onSelectLesson}
                                    />
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </Box>
    )
}

function LessonRow({ lesson, sequential, isCurrent, onSelect }) {

    const duration = formatVLearnDuration(lesson.duration_seconds)
    const locked = !!lesson.locked
    const completed = lesson.my_status === 'completed'
    const inProgress = lesson.my_status === 'in_progress'

    const row = (
        <ListItemButton
            disabled={locked}
            selected={isCurrent}
            onClick={locked ? undefined : () => onSelect && onSelect(lesson.id)}
            // `aria-current="step"` rather than "page": the lesson is a position within the
            // course, not a separate document.
            aria-current={isCurrent ? 'step' : undefined}
            sx={{
                alignItems: 'flex-start', gap: 1.25, px: 1.5, py: 1.1,
                borderLeft: '3px solid transparent',
                '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 144, 21, 0.09)',
                    borderLeftColor: 'var(--orange, rgb(255,144,21))',
                    '&:hover': { backgroundColor: 'rgba(255, 144, 21, 0.14)' }
                },
                '&:focus-visible': {
                    outline: '2px solid var(--orange, rgb(255,144,21))',
                    outlineOffset: '-2px'
                }
            }}
        >
            <Box sx={{ mt: '2px', flexShrink: 0, display: 'flex' }}>
                <VLearnTypeIcon type={lesson.type} sx={locked ? { color: '#b3b8c2' } : undefined} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{
                    fontSize: '0.83rem',
                    fontWeight: isCurrent ? 600 : 500,
                    lineHeight: 1.35,
                    color: locked ? 'var(--text-muted, #8b92a0)' : 'var(--text-primary, #111318)'
                }}>
                    {lesson.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                    {duration ? (
                        <Typography sx={{ ...vlearnType.meta }}>{duration}</Typography>
                    ) : null}
                    {/*
                      * BOTH states are labelled, and that is the point.
                      *
                      * Only "Required" was drawn, so an optional lesson said nothing — and
                      * since completion counts only mandatory lessons, a course whose lessons
                      * are mostly optional showed 100% while the sidebar showed 3 of 5 done,
                      * with nothing on screen to explain the difference.
                      */}
                    {lesson.is_mandatory ? (
                        <Typography sx={{ ...vlearnType.meta, color: '#B3271F', fontWeight: 600 }}>
                            <Langs str="VLEARN_REQUIRED" />
                        </Typography>
                    ) : (
                        <Typography sx={{ ...vlearnType.meta, fontStyle: 'italic' }}>
                            <Langs str="VLEARN_OPTIONAL" />
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box sx={{ flexShrink: 0, mt: '2px', display: 'flex', alignItems: 'center' }}>
                {locked ? (
                    <LockOutlinedIcon
                        sx={{ fontSize: 16, color: '#b3b8c2' }}
                        role="img"
                        aria-label={Localize('VLEARN_LESSON_LOCKED_ARIA')}
                    />
                ) : completed ? (
                    <CheckCircleIcon
                        sx={{ fontSize: 17, color: 'var(--green, rgb(58,156,96))' }}
                        role="img"
                        aria-label={Localize('VLEARN_LESSON_COMPLETED_ARIA')}
                    />
                ) : inProgress ? (
                    <Box
                        role="img"
                        aria-label={Localize('VLEARN_LESSON_IN_PROGRESS_ARIA')}
                        sx={{
                            width: 9, height: 9, borderRadius: '50%',
                            backgroundColor: 'var(--orange, rgb(255,144,21))'
                        }}
                    />
                ) : null}
            </Box>
        </ListItemButton>
    )

    if (!locked) return row

    // MUI removes pointer events from a disabled button, and a Tooltip needs an element that
    // still receives them — hence the wrapper rather than a tooltip on the row itself.
    return (
        <Tooltip
            arrow
            title={<Langs str={sequential ? 'VLEARN_LOCKED_TOOLTIP' : 'VLEARN_LOCKED_TOOLTIP_GENERIC'} />}
        >
            <Box>{row}</Box>
        </Tooltip>
    )
}
